// src/pages/Landing/Landing.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Bot,
  Brain,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Compass,
  GraduationCap,
  Smartphone,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Trophy,
  Users,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import Layout from "../../components/Layout/Layout";
import "./Landing.css";

const testimonials = [
  {
    name: "Sarah Johnson",
    role: "High School Student",
    text: "MyEduGenius transformed my study habits! The AI tutor helped me understand complex math concepts in minutes.",
    rating: 5,
  },
  {
    name: "Michael Chen",
    role: "Physics Teacher",
    text: "I save 10+ hours weekly preparing lessons. The curriculum viewer and quiz generator are game-changers!",
    rating: 5,
  },
  {
    name: "Emma Williams",
    role: "Biology Student",
    text: "The personalized learning paths and progress tracking kept me motivated throughout my studies.",
    rating: 5,
  },
];

const features = [
  {
    icon: Bot,
    title: "AI-Powered Learning",
    description:
      "Get instant help from specialized AI agents tailored to your learning style and pace.",
  },
  {
    icon: BarChart3,
    title: "Smart Progress Tracking",
    description:
      "Monitor your learning journey with detailed analytics and personalized insights.",
  },
  {
    icon: Target,
    title: "Adaptive Curriculum",
    description:
      "Learn with content that adapts to your knowledge level and learning preferences.",
  },
  {
    icon: Trophy,
    title: "Achievement System",
    description:
      "Stay motivated with gamified learning experiences and milestone rewards.",
  },
  {
    icon: Users,
    title: "Collaborative Learning",
    description:
      "Connect with peers and teachers in an interactive educational environment.",
  },
  {
    icon: Smartphone,
    title: "Multi-Platform Access",
    description:
      "Learn anywhere, anytime with our responsive web and mobile applications.",
  },
];

const agents = [
  {
    name: "AI Tutor",
    desc: "Get personalized explanations and step-by-step guidance",
    icon: Brain,
  },
  {
    name: "Quiz Master",
    desc: "Practice with intelligent quizzes and instant feedback",
    icon: ClipboardCheck,
  },
  {
    name: "Progress Tracker",
    desc: "Monitor your learning journey and achievements",
    icon: TrendingUp,
  },
  {
    name: "Study Buddy",
    desc: "Personalized study plans and learning recommendations",
    icon: Compass,
  },
];

const steps = [
  {
    icon: GraduationCap,
    title: "Create your account",
    text: "Sign up as a student or teacher and pick your class level.",
  },
  {
    icon: Bot,
    title: "Learn with AI agents",
    text: "Ask the tutor, take quizzes and summarize notes — aligned to your curriculum.",
  },
  {
    icon: TrendingUp,
    title: "Track your growth",
    text: "Streaks, points and analytics show exactly where to focus next.",
  },
];

const stats = [
  { label: "Active Students", value: "50,000+" },
  { label: "AI Interactions", value: "1M+" },
  { label: "Lessons Completed", value: "250,000+" },
  { label: "Success Rate", value: "94%" },
];

const initials = (name) =>
  name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2);

const Landing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  // Redirect authenticated users
  useEffect(() => {
    if (user) {
      navigate("/home");
    }
  }, [user, navigate]);

  const nextTestimonial = () =>
    setTestimonialIndex((prev) => (prev + 1) % testimonials.length);

  const prevTestimonial = () =>
    setTestimonialIndex(
      (prev) => (prev - 1 + testimonials.length) % testimonials.length
    );

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(nextTestimonial, 6000);
    return () => clearInterval(interval);
  }, []);

  const testimonial = testimonials[testimonialIndex];

  return (
    <Layout fullBleed>
      <div className="landing-page">
        {/* Hero */}
        <section className="lp-hero">
          <div className="lp-hero-inner">
            <div className="lp-hero-copy">
              <span className="lp-hero-badge">
                <Sparkles size={14} />
                Now with Advanced AI Technology
              </span>

              <h1>
                Revolutionize your learning with{" "}
                <span className="lp-highlight">AI-powered education</span>
              </h1>

              <p className="lp-hero-lead">
                Join thousands of students and teachers using MyEduGenius to
                transform education. Get personalized tutoring, smart
                assessments, and progress tracking powered by cutting-edge AI.
              </p>

              <div className="lp-hero-actions">
                <button
                  onClick={() => navigate("/register")}
                  className="eg-btn eg-btn-accent eg-btn-lg"
                >
                  Start learning free
                  <ArrowRight size={18} />
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="eg-btn eg-btn-ghost-light eg-btn-lg"
                >
                  Sign in
                </button>
              </div>

              <ul className="lp-hero-points">
                <li><CheckCircle2 size={16} /> Free for students</li>
                <li><CheckCircle2 size={16} /> Curriculum-aligned</li>
                <li><CheckCircle2 size={16} /> Built for teachers too</li>
              </ul>
            </div>

            <div className="lp-hero-visual" aria-hidden="true">
              <div className="lp-chat-card">
                <div className="lp-chat-head">
                  <span className="lp-chat-avatar"><Bot size={18} /></span>
                  <div>
                    <strong>AI Tutor</strong>
                    <span className="lp-chat-status">Online · Mathematics</span>
                  </div>
                </div>
                <div className="lp-chat-body">
                  <div className="lp-bubble lp-bubble-ai">
                    I can help you understand calculus derivatives. Would you
                    like me to explain the power rule?
                  </div>
                  <div className="lp-bubble lp-bubble-user">
                    Yes, please explain it step by step
                  </div>
                  <div className="lp-bubble lp-bubble-ai lp-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>

              <div className="lp-float lp-float-streak">
                <span className="lp-float-icon"><Trophy size={18} /></span>
                <div>
                  <strong>7-day streak</strong>
                  <span>Keep it going!</span>
                </div>
              </div>

              <div className="lp-float lp-float-score">
                <span className="lp-float-icon lp-float-icon-navy"><ClipboardCheck size={18} /></span>
                <div>
                  <strong>Quiz score 9/10</strong>
                  <span>Algebra · JSS2</span>
                </div>
              </div>
            </div>
          </div>

          <div className="lp-stats">
            {stats.map((stat) => (
              <div key={stat.label} className="lp-stat">
                <span className="lp-stat-value">{stat.value}</span>
                <span className="lp-stat-label">{stat.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="lp-section">
          <div className="container">
            <div className="lp-section-head">
              <span className="eg-eyebrow">How it works</span>
              <h2>Up and running in three steps</h2>
            </div>
            <div className="lp-steps">
              {steps.map((step, index) => (
                <div key={step.title} className="lp-step">
                  <span className="lp-step-num">{index + 1}</span>
                  <step.icon size={22} className="lp-step-icon" />
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="features" className="lp-section lp-section-tint">
          <div className="container">
            <div className="lp-section-head">
              <span className="eg-eyebrow">Features</span>
              <h2>Everything you need to excel</h2>
              <p>
                Powerful AI-driven features designed to accelerate your learning
                journey
              </p>
            </div>

            <div className="lp-features">
              {features.map((feature) => (
                <div key={feature.title} className="lp-feature">
                  <span className="lp-feature-icon">
                    <feature.icon size={22} />
                  </span>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* AI Agents */}
        <section className="lp-section">
          <div className="container">
            <div className="lp-section-head">
              <span className="eg-eyebrow">AI Agents</span>
              <h2>Meet your learning companions</h2>
              <p>
                Specialized AI agents working together to enhance your
                educational experience
              </p>
            </div>

            <div className="lp-agents">
              {agents.map((agent) => (
                <button
                  key={agent.name}
                  type="button"
                  className="lp-agent"
                  onClick={() => navigate("/register")}
                >
                  <span className="lp-agent-icon">
                    <agent.icon size={24} />
                  </span>
                  <h3>{agent.name}</h3>
                  <p>{agent.desc}</p>
                  <span className="lp-agent-link">
                    Try now <ArrowRight size={16} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="lp-section lp-section-tint">
          <div className="container">
            <div className="lp-section-head">
              <span className="eg-eyebrow">Testimonials</span>
              <h2>What our users say</h2>
            </div>

            <div className="lp-testimonial">
              <button
                className="lp-testimonial-nav"
                onClick={prevTestimonial}
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={20} />
              </button>

              <figure className="lp-testimonial-card" key={testimonialIndex}>
                <div className="lp-stars" aria-label={`${testimonial.rating} out of 5 stars`}>
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} size={18} fill="currentColor" />
                  ))}
                </div>
                <blockquote>“{testimonial.text}”</blockquote>
                <figcaption>
                  <span className="lp-author-avatar">{initials(testimonial.name)}</span>
                  <span>
                    <strong>{testimonial.name}</strong>
                    <span>{testimonial.role}</span>
                  </span>
                </figcaption>
              </figure>

              <button
                className="lp-testimonial-nav"
                onClick={nextTestimonial}
                aria-label="Next testimonial"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="lp-dots">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`lp-dot ${index === testimonialIndex ? "active" : ""}`}
                  onClick={() => setTestimonialIndex(index)}
                  aria-label={`Show testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="lp-cta-wrap">
          <div className="container">
            <div className="lp-cta">
              <BookOpen size={28} className="lp-cta-icon" />
              <h2>Start your learning journey today</h2>
              <p>
                Join thousands of students and teachers already using MyEduGenius
                to achieve their educational goals.
              </p>
              <div className="lp-cta-actions">
                <button
                  onClick={() => navigate("/register")}
                  className="eg-btn eg-btn-accent eg-btn-lg"
                >
                  Get started free
                  <ArrowRight size={18} />
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="eg-btn eg-btn-ghost-light eg-btn-lg"
                >
                  I already have an account
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Landing;
